/**
 * Debug logger utility
 *
 * Purpose: Provides a flexible logging system with module-specific loggers,
 * consistent formatting, and environment-based toggling.
 *
 * How it works:
 * 1. Checks environment variables to determine if logging is enabled
 * 2. Creates module-specific loggers with consistent formatting
 * 3. Provides standard log levels and grouping functionality
 *
 * Key features:
 * - Module-specific loggers with consistent formatting
 * - Environment-controlled debug mode
 * - Support for log levels (info, warn, error)
 * - Console grouping support
 * - Optional data logging
 */

// Master debug flag from environment
const DEBUG_ENABLED = import.meta.env.VITE_DEBUG === "true";

// Optional module-specific debug flags
const DEBUG_MODULES: Record<string, boolean> = {
  // Add module-specific overrides here
  // Example: "EventsSync": true, // Always enable EventsSync logs
  ChatsSync: false,
  EventsSync: false,
  Socket: false,
  Reconciliation: false,
  AppNotification: false,
  Visibility: false,
  MessageHandler: false,
  UUID7: false,
};

/**
 * Logger icon set for different log types
 */
const ICONS = {
  info: "📋",
  warn: "⚠️",
  error: "❌",
  group: "🔍",
};

/**
 * Creates a module-specific logger with consistent formatting
 *
 * @param moduleName - The name of the module (appears in log prefix)
 * @returns A logger object with info, warn, error, group and groupEnd methods
 */
export function createLogger(moduleName: string) {
  // Check if this module's logging is enabled
  const isEnabled = () => {
    // Module-specific override if defined
    if (moduleName in DEBUG_MODULES) {
      return DEBUG_MODULES[moduleName];
    }
    // Otherwise use master debug flag
    return DEBUG_ENABLED;
  };

  return {
    info: (message: string, data?: unknown) => {
      if (!isEnabled()) {
        return;
      }
      // eslint-disable-next-line no-console
      console.log(
        `[${moduleName}] ${ICONS.info} ${message}`,
        data !== undefined ? data : "",
      );
    },
    warn: (message: string, data?: unknown) => {
      if (!isEnabled()) {
        return;
      }
      // eslint-disable-next-line no-console
      console.warn(
        `[${moduleName}] ${ICONS.warn} ${message}`,
        data !== undefined ? data : "",
      );
    },
    error: (message: string, data?: unknown) => {
      if (!isEnabled()) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error(
        `[${moduleName}] ${ICONS.error} ${message}`,
        data !== undefined ? data : "",
      );
    },
    group: (title: string) => {
      if (!isEnabled()) {
        return;
      }
      // eslint-disable-next-line no-console
      console.group(`[${moduleName}] ${ICONS.group} ${title}`);
    },
    groupEnd: () => {
      if (!isEnabled()) {
        return;
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
    },
  };
}
